<template>
  <v-layout>
    <div class="d-flex justify-end full-width" v-if="!createMode">
      <div class="mr-auto" v-if="node.meta.question">
        {{ node.meta.question }}
      </div>
      <div v-if="!editMode">
        {{ displayValue }}
        <span class="font-weight-light">{{ displayUOM }}</span>
      </div>

      <span v-if="editMode">
        <v-row class="pl-2">
          <v-col
            col="12"
            v-for="(item, index) in node.meta.value_name"
            :key="index"
          >
            <v-text-field
              :value="valueIsEmpty ? null : node.value[editIndex].val[index]"
              :rules="[checkMinAndMax]"
              :label="item"
              required
              type="number"
              @change="changeValue($event, node, editIndex, index)"
              :hint="hintText()"
              :disabled="valueIsEmpty"
            ></v-text-field>
            <div class="text-caption mt-1" v-if="node.meta.uom">
              Unit Of Measure is {{ node.meta.uom }}
            </div>
          </v-col>
        </v-row>
        <div v-if="valueIsEmpty" class="text-caption grey--text text--darken-2">
          This field is disabled until you select a date/time below
        </div>
      </span>
    </div>
    <v-form ref="form" v-else class="full-width">
      <div class="d-flex justify-space-between full-width">
        <v-text-field
          :value="arrayLength"
          :rules="[() => !!parseInt(arrayLength, 10) || 'An entry is required']"
          label="Number of entries"
          required
          type="number"
          @input="setArrayLength($event, node)"
          class="mr-4"
        ></v-text-field>
        <v-text-field
          v-model="node.meta.separator"
          label="Separator character (optional)"
          @input="validate()"
        ></v-text-field>
      </div>
      <div class="d-flex justify-space-between flex-wrap full-width">
        <v-text-field
          v-for="index in parseInt(arrayLength, 10)"
          :key="index"
          v-model="node.meta.value_name[index - 1]"
          :rules="[
            () => !!node.meta.value_name[index - 1] || 'An entry is required',
          ]"
          :label="`Name of entry ${index}`"
          required
          :class="{ 'mr-4': index % 3 && index !== parseInt(arrayLength, 10) }"
          @input="validate()"
        ></v-text-field>
      </div>
      <div class="d-flex justify-space-between full-width">
        <v-select
          class="mr-4"
          v-model="node.meta.precision"
          :items="precisionValues"
          label="Precision"
          hint="The number of digits after the decimal point."
        ></v-select>
        <v-text-field
          class="mr-4"
          type="number"
          label="Minimum (optional)"
          v-model.number="node.meta.min"
          @input="validate()"
          :rules="[minAndMax]"
        ></v-text-field>
        <v-text-field
          type="number"
          label="Maximum (optional)"
          v-model.number="node.meta.max"
          @input="validate()"
          :rules="[minAndMax]"
        ></v-text-field>
      </div>
      <div>
        <AddUnits v-model="node.meta.uom" />
      </div>
      <v-row>
        <v-col>
          <div class="d-flex justify-space-between full-width">
            <v-text-field
              label="Question (optional)"
              v-model="node.meta.question"
              class="mr-4"
            ></v-text-field>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <div class="d-flex justify-space-between full-width">
            <v-text-field
              label="Tooltip (optional)"
              v-model="node.meta.tooltip"
              class="mr-4"
            ></v-text-field>
          </div>
        </v-col>
      </v-row>
    </v-form>
  </v-layout>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";
import AddUnits from "../record-viewer/AddUnits";

import isArray from "lodash/isArray";
import isFinite from "lodash/isFinite";

export default {
  name: "NumericArray",
  components: { AddUnits },
  props: {
    node: Object,
    editMode: Boolean,
    editIndex: Number,
    createMode: { type: Boolean, default: false, required: false },
    selectedRecord: Object,
    isRoot: Boolean,
  },
  created() {
    this.precisionValues = [
      { text: "Integer", value: 0 },
      { text: "0.1", value: 1 },
      { text: "0.01", value: 2 },
      { text: "0.001", value: 3 },
    ];
  },
  mounted() {
    if (this.createMode) {
      this.validate();
    }
  },
  data() {
    return {
      arrayLength: this.node.meta?.value_name
        ? this.node.meta.value_name.length
        : 0,
    };
  },
  watch: {
    editIndex: function(newVal, oldVal) {
      if (newVal !== oldVal) {
        // If we're going into edit mode, make sure this entry is an array, not a string
        if (!isArray(this.node.value[this.editIndex].val)) {
          this.convertFromCSVToArray();
        }
      }
    },
  },
  computed: {
    ...mapGetters("allUoms", { units: "list" }, { root: true }),
    ...mapGetters({
      loginDetails: "getUser",
      getName: "getLoggedInName",
    }),
    displayValue: function() {
      if (this.node.value.length) {
        if (!isArray(this.node.value[this.node.value.length - 1].val)) {
          this.convertFromCSVToArray();
        }
        const sep = this.node.meta.separator ? this.node.meta.separator : ",";
        return this.node.value[this.node.value.length - 1].val.join(sep);
      }
      return "";
    },
    displayUOM: function() {
      if (
        this.node.value.length &&
        this.node.value[this.editIndex].val.length >=
          this.node.meta.value_name.length
      ) {
        return this.node.meta.uom;
      } else {
        return "";
      }
    },
    valueIsEmpty: function() {
      return this.node.value.length === 0;
    },
  },
  methods: {
    changeValue(e, node, editIndex, index) {
      if (!e) {
        return;
      }
      let newValue = parseFloat(e);
      const digitShifter = Math.pow(10, node.meta.precision);
      newValue = Math.round(e * digitShifter) / digitShifter;

      this.$store.commit("UPDATE_NODE_PERIODIC_VALUE", {
        id: node._id,
        editIndex,
        index,
        newValue,
      });
      node.value[this.editIndex].authorName = this.getName;
      node.value[this.editIndex].authorId = this.loginDetails.id;

      this.$root.$emit("edit:update");
    },
    convertFromCSVToArray() {
      if (!isArray(this.node.value[this.editIndex].val)) {
        let newValues;
        try {
          newValues = this.node.value[this.editIndex].val.split(",");
        } catch {
          newValues = new Array(this.node.meta.value_name.length);
        }
        Vue.set(this.node.value[this.editIndex], "val", newValues);
      }
    },
    setArrayLength(e, node) {
      this.arrayLength = e;
      const newLength = parseInt(e, 10);
      if (Number.isNaN(newLength)) {
        return;
      }

      node.value = new Array(newLength);
      if (!node.meta.value_name || !node.meta.value_name.length) {
        Vue.set(node.meta, "value_name", new Array(newLength));
      } else {
        if (node.meta.value_name.length < newLength) {
          const extraLength = newLength - node.meta.value_name.length;
          for (let i = 0; i < extraLength; i++) {
            node.meta.value_name.push(
              `${this.node.name} ${node.meta.value_name.length + i}`
            );
          }
        } else if (node.meta.value_name.length > newLength) {
          const smallerLength = node.meta.value_name.length - newLength;
          node.meta.value_name.splice(-smallerLength);
        }
      }
    },
    validate() {
      const okay = this.$refs.form.validate();
      this.$emit("validation", okay);
    },
    minAndMax() {
      if (isFinite(this.node.meta.min) && isFinite(this.node.meta.max)) {
        return (
          this.node.meta.min < this.node.meta.max ||
          "The minimum must be less than the maximum"
        );
      }
      return true;
    },
    checkMinAndMax(ev) {
      const e = parseFloat(ev, 10);
      if (!isFinite(e)) {
        return "A number is required";
      }
      if (isFinite(this.node.meta.min) && e < this.node.meta.min) {
        return `The value must be greater than or equal to ${this.node.meta.min}`;
      }
      if (isFinite(this.node.meta.max) && e > this.node.meta.max) {
        return `The value must be less than or equal to ${this.node.meta.max}`;
      }
      return true;
    },
    hintText() {
      let result = "";
      if (isFinite(this.node.meta.min) && isFinite(this.node.meta.max)) {
        result = `The value must be between ${this.node.meta.min} and ${this.node.meta.max}. `;
      }
      if (this.node.meta.precision) {
        result += `The value will be rounded to ${
          this.node.meta.precision
        } decimal place${this.node.meta.precision === 1 ? "" : "s"}.`;
      } else {
        result += "The value will be rounded to an integer.";
      }
      return result;
    },
  },
};
</script>

<style lang="scss" scoped></style>
